import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { toast } from "sonner";
import type {User} from "../types";
import { UserProfile } from "../utils/compatibilityAI";
import { mapApiProfileToFavoriteUser } from "../utils/mapApiProfile";
import { addFavorite, listFavorites, removeFavorite } from "../services/favoritesService";
import { useAuth } from "./AuthContext";

interface FavoritesContextType {
  favorites: User[];
  loading: boolean;
  refreshFavorites: () => Promise<void>;
  addToFavorites: (user: User) => void;
  removeFromFavorites: (userId: string) => void;
  isFavorite: (userId: string | number) => boolean;
  toggleFavorite: (user: User | UserProfile) => void;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

function toFavoriteUser(user: User | UserProfile): User {
  if ("photos" in user && Array.isArray((user as User).photos)) {
    return user as User;
  }
  const p = user as UserProfile;
  return {
    id: String(p.id),
    name: p.name,
    age: p.age,
    location: p.location,
    bio: p.bio,
    photos: p.photo ? [p.photo] : [],
    interests: p.interests,
    online: false,
  };
}

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [favorites, setFavorites] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);

  const refreshFavorites = useCallback(async () => {
    setLoading(true);
    const res = await listFavorites();
    setLoading(false);
    if (res.error) {
      return;
    }
    const users = (res.data ?? []).map((row) => mapApiProfileToFavoriteUser(row.user));
    setFavorites(users);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      setFavorites([]);
      return;
    }
    void refreshFavorites();
  }, [isAuthenticated, authLoading, refreshFavorites]);

  const addToFavorites = useCallback((user: User) => {
    setFavorites((prev) => (prev.some((u) => u.id === user.id) ? prev : [...prev, user]));
    void (async () => {
      const res = await addFavorite(user.id);
      if (res.error) {
        setFavorites((prev) => prev.filter((u) => u.id !== user.id));
        toast.error(res.error);
      }
    })();
  }, []);

  const removeFromFavorites = useCallback((userId: string) => {
    let removed: User | undefined;
    setFavorites((prev) => {
      removed = prev.find((u) => u.id === userId);
      return prev.filter((u) => u.id !== userId);
    });
    void (async () => {
      const res = await removeFavorite(userId);
      if (res.error && removed) {
        setFavorites((prev) =>
          prev.some((u) => u.id === userId) ? prev : [...prev, removed!]
        );
        toast.error(res.error);
      }
    })();
  }, []);

  const isFavorite = useCallback(
    (userId: string | number) => favorites.some((u) => u.id === String(userId)),
    [favorites]
  );

  const toggleFavorite = useCallback(
    (user: User | UserProfile) => {
      const u = toFavoriteUser(user);
      if (isFavorite(u.id)) removeFromFavorites(u.id);
      else addToFavorites(u);
    },
    [addToFavorites, isFavorite, removeFromFavorites]
  );

  const value = useMemo(
    () => ({
      favorites,
      loading,
      refreshFavorites,
      addToFavorites,
      removeFromFavorites,
      isFavorite,
      toggleFavorite,
    }),
    [
      favorites,
      loading,
      refreshFavorites,
      addToFavorites,
      removeFromFavorites,
      isFavorite,
      toggleFavorite,
    ]
  );

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}

export function useFavorites() {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error("useFavorites must be used within FavoritesProvider");
  }
  return context;
}

/** Storybook-only provider with static favorites (no API). */
export function MockFavoritesProvider({
  children,
  favorites = [],
}: {
  children: ReactNode;
  favorites?: User[];
}) {
  const value: FavoritesContextType = {
    favorites,
    loading: false,
    refreshFavorites: async () => {},
    addToFavorites: () => {},
    removeFromFavorites: () => {},
    isFavorite: (userId) => favorites.some((u) => u.id === String(userId)),
    toggleFavorite: () => {},
  };
  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}
